import type { ApiQaAgentResult } from '../utils/api'

// ── §32+§33: AI QA Agent Report Panel ─────────────────────────────────────────

const SEV_COLOR: Record<string, string> = {
  critical: '#ef4444',
  high:     '#f59e0b',
  medium:   '#3b82f6',
  low:      '#6b7280',
}

const SEV_BG: Record<string, string> = {
  critical: '#fef2f2',
  high:     '#fffbeb',
  medium:   '#eff6ff',
  low:      '#f9fafb',
}

const SEV_LABEL: Record<string, string> = {
  critical: 'Критично',
  high:     'Высокий',
  medium:   'Средний',
  low:      'Низкий',
}

interface Props {
  result: ApiQaAgentResult
  onClose?: () => void
}

export function QaReportPanel({ result, onClose }: Props) {
  const total = result.passedTests + result.failedTests
  const passRate = total > 0 ? Math.round((result.passedTests / total) * 100) : 0
  const passColor = passRate >= 80 ? '#22c55e' : passRate >= 60 ? '#f59e0b' : '#ef4444'

  const criticalIssues = result.issues.filter(i => i.severity === 'critical')
  const highIssues     = result.issues.filter(i => i.severity === 'high')
  const otherIssues    = result.issues.filter(i => i.severity !== 'critical' && i.severity !== 'high')

  return (
    <div style={{ fontFamily: 'system-ui, sans-serif', color: '#111' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%', flexShrink: 0,
            border: `3px solid ${passColor}`, display: 'flex', alignItems: 'center',
            justifyContent: 'center', flexDirection: 'column', background: `${passColor}18`,
          }}>
            <span style={{ fontSize: 20, fontWeight: 800, color: passColor, lineHeight: 1 }}>{passRate}%</span>
            <span style={{ fontSize: 9, color: '#6b7280', lineHeight: 1 }}>пройдено</span>
          </div>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 4 }}>AI QA Тест</div>
            <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>{result.summary}</div>
            <div style={{ marginTop: 6, display: 'flex', gap: 10, fontSize: 12 }}>
              <span style={{ color: '#22c55e', fontWeight: 700 }}>✓ {result.passedTests} пройдено</span>
              <span style={{ color: '#ef4444', fontWeight: 700 }}>✗ {result.failedTests} провалено</span>
            </div>
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9ca3af' }}>×</button>
        )}
      </div>

      {/* Overall result badge */}
      <div style={{
        padding: '10px 14px', borderRadius: 10, marginBottom: 16,
        background: passRate >= 80 ? '#f0fdf4' : passRate >= 60 ? '#fffbeb' : '#fef2f2',
        border: `1px solid ${passColor}44`,
        fontSize: 13, color: '#374151', lineHeight: 1.5,
      }}>
        <span style={{ fontWeight: 700, color: passColor }}>
          {result.overallResult === 'pass' ? '✅ Тест пройден' : result.overallResult === 'fail' ? '❌ Тест провален' : '⚠️ Частично'}
        </span>{' — '}
        {result.summary}
      </div>

      {/* Issues */}
      {result.issues.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 32, color: '#22c55e', fontSize: 13 }}>
          ✓ Все тесты пройдены успешно
        </div>
      ) : (
        <div>
          {[
            { label: 'Критические', items: criticalIssues },
            { label: 'Высокий приоритет', items: highIssues },
            { label: 'Прочие', items: otherIssues },
          ].map(({ label, items }) => items.length > 0 && (
            <div key={label} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: SEV_COLOR[items[0]?.severity ?? 'low'], textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                {label} ({items.length})
              </div>
              {items.map((issue, i) => (
                <div key={i} style={{
                  background: SEV_BG[issue.severity] ?? '#f9fafb',
                  border: `1px solid ${SEV_COLOR[issue.severity] ?? '#6b7280'}33`,
                  borderRadius: 8, padding: 10, marginBottom: 6,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{
                      fontSize: 10, fontWeight: 700,
                      color: SEV_COLOR[issue.severity] ?? '#6b7280',
                      textTransform: 'uppercase',
                      background: (SEV_COLOR[issue.severity] ?? '#6b7280') + '22',
                      padding: '2px 6px', borderRadius: 4,
                    }}>
                      {SEV_LABEL[issue.severity] ?? issue.severity}
                    </span>
                    {issue.page && (
                      <span style={{ fontSize: 11, color: '#9ca3af' }}>/ {issue.page}</span>
                    )}
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#111', marginLeft: 'auto' }}>{issue.test}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#4b5563', marginBottom: 4 }}>{issue.description}</div>
                  <div style={{ fontSize: 11, color: SEV_COLOR[issue.severity] ?? '#6b7280', display: 'flex', gap: 4 }}>
                    <span style={{ fontWeight: 600 }}>→ Решение:</span>
                    <span>{issue.fixSuggestion}</span>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
